import {connect} from 'react-redux';

import {
  ComposerAiState,
  ComposerAiStatesEnum,
} from '../../../../../libs/shared/core/src/helpers/ai/composeV2Helpers';
import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {MessageErrorModel} from '../../../../../libs/shared/core/src/models/api/messageErrorModel';
import {DraftMessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {getHasResolveDrafts} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ResolveStateProps} from '../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {withUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  AppParametricSelector,
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {ComposerVariablesState} from '../../../../helpers/models/applicationObjectVariableHelpers';
import {buildChannelSettingsPath, ChannelErrorsEnum} from '../../../../helpers/models/channelHelpers';
import {
  isDraftInNewConversation,
  isForwardingToTeammate,
  isMassSend,
  SendDraftErrorsEnum,
} from '../../../../helpers/models/messageHelpers';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {AppState} from '../../../../store/appState';
import {getApplicationFromObjectUid} from '../../../../store/data/applications/applicationsSelectors';
import {makeGetConversationTopics} from '../../../../store/data/conversations/conversationsSelectors';
import {getContacts} from '../../../../store/data/dataSelectors';
import {togglePasteAsTextAction} from '../../../../store/data/preferences/preferencesActions';
import {getMentionableEmails} from '../../../../store/data/teammates/mentionTeammatesSelectors';
import {makeGetApplicationObjectTopics, makeGetTopic} from '../../../../store/data/topics/topicsSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {
  ComposerBanner,
  ComposerBannerErrorsEnum,
  ComposerBannerLevelsEnum,
  ComposerBannerOptions,
  MessageTipsEnum,
} from './composerBanner';

/*
 * Props.
 */

interface ComposerBannerContainerProps {
  message: DraftMessageModel;
  validationErrors: ReadonlyArray<ComposerBannerErrorsEnum>;
  hasRawVariables?: boolean;
  hasOnlyApplicationRawVariables?: boolean;
  saveError?: MessageErrorModel;
  attemptedSendDraftAttachments: boolean;
  dismissAttemptedSendDraftAttachments: () => void;
  conflictLinkAction?: () => void;
  channel: ChannelModel;
  isChatComposer?: boolean;
  hasPasteAsTextBeenIgnored?: boolean;
  dismissIgnoredPasteAsText?: () => void;
  conversation: ConversationModel | null;
  variablesState?: ComposerVariablesState;
  aiState?: ComposerAiState;
  dismissAi?: () => void;
}

/*
 * Selectors.
 */

type GetValidationErrors = AppParametricSelector<any, ReadonlyArray<ComposerBannerErrorsEnum>>;

function getMessage(_state: AppState, props: ComposerBannerContainerProps) {
  return props.message;
}

function getValidationErrors(_state: AppState, props: ComposerBannerContainerProps) {
  return props.validationErrors;
}

function getHasRawVariables(_state: AppState, props: ComposerBannerContainerProps) {
  return props.hasRawVariables;
}

function getHasOnlyApplicationRawVariables(_state: AppState, props: ComposerBannerContainerProps) {
  return props.hasOnlyApplicationRawVariables;
}

function makeGetBannerValidationErrors(getValidationErrorsSelector: GetValidationErrors) {
  return createSelector([getValidationErrorsSelector], (validationErrors) =>
    validationErrors.filter((error) => error !== SendDraftErrorsEnum.INCOMPLETE),
  );
}

function getSaveError(_state: AppState, props: ComposerBannerContainerProps) {
  return props.saveError;
}

function getAttemptedSendDraftAttachments(_state: AppState, props: ComposerBannerContainerProps) {
  return props.attemptedSendDraftAttachments;
}

function getDismissAttemptedSendDraftAttachments(_state: AppState, props: ComposerBannerContainerProps) {
  return props.dismissAttemptedSendDraftAttachments;
}

function getDismissIgnoredPasteAsText(_state: AppState, props: ComposerBannerContainerProps) {
  return props.dismissIgnoredPasteAsText;
}

function getHasPasteAsTextBeenIgnored(_state: AppState, props: ComposerBannerContainerProps) {
  return props.hasPasteAsTextBeenIgnored;
}

function getConflictLinkAction(_state: AppState, props: ComposerBannerContainerProps) {
  return props.conflictLinkAction;
}

function getConversation(_state: AppState, props: ComposerBannerContainerProps) {
  return props.conversation ?? undefined;
}

function getObjectUid(_state: AppState, props: ComposerBannerContainerProps) {
  return props.variablesState?.objectUid;
}

function getTopicId(_state: AppState, props: ComposerBannerContainerProps) {
  return props.variablesState?.topicId;
}

function getAiState(_state: AppState, props: ComposerBannerContainerProps) {
  return props.aiState;
}

function getDismissAi(_state: AppState, props: ComposerBannerContainerProps) {
  return props.dismissAi;
}

function makeGetBannerOptions() {
  return createSelector(
    [
      getMessage,
      makeGetBannerValidationErrors(getValidationErrors),
      getHasRawVariables,
      getHasOnlyApplicationRawVariables,
      getSaveError,
      getMentionableEmails,
      getContacts,
      getConflictLinkAction,
      getAttemptedSendDraftAttachments,
      getDismissAttemptedSendDraftAttachments,
      getChannelErrorType,
      getDismissIgnoredPasteAsText,
      getHasPasteAsTextBeenIgnored,
      makeGetApplicationObjectTopics(getObjectUid, makeGetConversationTopics(getConversation)),
      getAiState,
      getDismissAi,
    ],
    (
      message,
      bannerValidationErrors,
      hasRawVariables,
      hasOnlyApplicationRawVariables,
      saveError,
      mentionableEmails,
      contacts,
      conflictLinkAction,
      attemptedSendDraftAttachments,
      dismissAttemptedSendDraftAttachments,
      channelErrorType,
      dismissIgnoredPasteAsText,
      hasPasteAsTextBeenIgnored,
      applicationObjectTopics,
      aiState,
      dismissAi,
    ): ComposerBannerOptions | undefined => {
      if (aiState?.state === ComposerAiStatesEnum.RUNNING) {
        return {
          type: aiState.feature.type,
          level: ComposerBannerLevelsEnum.INFORMATION,
        };
      }

      if (aiState?.state === ComposerAiStatesEnum.COMPLETED) {
        return {
          type: aiState.feature.type,
          level: ComposerBannerLevelsEnum.INFORMATION,
          dismissAction: dismissAi,
        };
      }

      if (aiState?.state === ComposerAiStatesEnum.ERROR) {
        return {
          type: aiState.feature.type,
          level: ComposerBannerLevelsEnum.INFORMATION,
          dismissAction: dismissAi,
        };
      }

      if (bannerValidationErrors.length > 0) {
        return {type: bannerValidationErrors[0], level: ComposerBannerLevelsEnum.WARNING};
      }

      if (saveError) {
        return {
          type: saveError.type,
          description: saveError.description,
          level: ComposerBannerLevelsEnum.WARNING,
        };
      }

      if (conflictLinkAction) {
        return {
          type: MessageTipsEnum.CONFLICTING_COPY,
          level: ComposerBannerLevelsEnum.INFORMATION,
          linkAction: conflictLinkAction,
        };
      }

      if (isMassSend(message)) {
        return {type: MessageTipsEnum.MASS_SEND, level: ComposerBannerLevelsEnum.INFORMATION};
      }

      if (
        isForwardingToTeammate(contacts, mentionableEmails, message) &&
        !isDraftInNewConversation(message)
      ) {
        return {type: MessageTipsEnum.TEAMMATE_FORWARD, level: ComposerBannerLevelsEnum.INFORMATION};
      }

      if (attemptedSendDraftAttachments) {
        return {
          type: MessageTipsEnum.HAS_DRAFT_ATTACHMENTS,
          level: ComposerBannerLevelsEnum.WARNING,
          dismissAction: dismissAttemptedSendDraftAttachments,
        };
      }

      if (channelErrorType === ChannelErrorsEnum.MISSING_O365_SEND_PERMISSIONS) {
        return {
          type: ChannelErrorsEnum.MISSING_O365_SEND_PERMISSIONS,
          level: ComposerBannerLevelsEnum.WARNING,
        };
      }

      // Show a warning banner if there are any raw variables.
      if (hasRawVariables) {
        // If all raw variables are application variables and there are multiple topic matches,
        // the warning message will be specific to application variables and also allow the user to change the selected topic.
        if (hasOnlyApplicationRawVariables && applicationObjectTopics.length > 1) {
          return {
            type: MessageTipsEnum.HAS_RAW_APP_VARIABLES,
            level: ComposerBannerLevelsEnum.WARNING,
          };
        }
        return {
          type: MessageTipsEnum.HAS_RAW_VARIABLES,
          level: ComposerBannerLevelsEnum.WARNING,
        };
      }

      if (applicationObjectTopics.length > 1) {
        return {
          type: MessageTipsEnum.HAS_APP_VARIABLES,
          level: ComposerBannerLevelsEnum.INFORMATION,
        };
      }

      if (hasPasteAsTextBeenIgnored && dismissIgnoredPasteAsText) {
        return {
          type: MessageTipsEnum.IGNORED_PASTE_AS_TEXT,
          level: ComposerBannerLevelsEnum.INFORMATION,
          dismissAction: dismissIgnoredPasteAsText,
        };
      }

      return undefined;
    },
  );
}

// Selectors

const getChannel = (_state: AppState, props: ComposerBannerContainerProps) => props.channel;

const getChannelErrorType = createSelector([getChannel], (channel) => channel?.error?.type);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ComposerBannerContainerProps>()(() => ({
  options: makeGetBannerOptions(),
  hasResolveDrafts: getHasResolveDrafts,
  applicationObjectTopics: makeGetApplicationObjectTopics(
    getObjectUid,
    makeGetConversationTopics(getConversation),
  ),
  application: getApplicationFromObjectUid(getObjectUid),
  selectedTopic: makeGetTopic(getTopicId),
}));

const mapDispatchToProps = makeMapDispatchToProps<ComposerBannerContainerProps>()(() => ({
  onNavigateToLink: navigateToLinkAction,
  togglePasteAsText: togglePasteAsTextAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ComposerBannerContainerProps,
) {
  const {channel} = ownProps;
  const {onNavigateToLink, togglePasteAsText} = dispatchProps;

  const onOpenChannelSettings = () => {
    if (!channel) {
      return;
    }

    onNavigateToLink(buildChannelSettingsPath(channel));
  };

  const onDisablePasteAsText = togglePasteAsText;

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onOpenChannelSettings,
    onDisablePasteAsText,
  };
}

export const ComposerBannerContainer = withUserTheme(
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(ComposerBanner),
);
